import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import { buildEmptyFilterState, serializeFilterState } from '../../commons/filters/GridFilterPanel.jsx';
import { buildPeriodoOptions, PERIODO_ESTE_ANIO } from '../../commons/filters/periodos.js';

/**
 * Declaración de los filtros de la grilla de Combustibles, para `GridFilterPanel` (VEC-3693).
 *
 * Va en un módulo aparte a propósito: lo consumen el panel Y la grilla, que necesita los mismos
 * campos para armar su query por defecto.
 *
 * *** EL QUERY DE ESTA GRILLA NO ES EL DEL PANEL. *** El panel serializa `base=1,2,3`, pero los
 * endpoints de combustibles esperan `bases[0][value]=1,2,3` —PHP lo recibe como un array de
 * `{value}` y lo explota por coma en `CombustiblesRepository::convertInSafeNumeric()`—.
 * Desde la fase 2 los cuatro desplegables que quedaban simples —marca, modelo, estado y
 * origen— son múltiples. El backend los acompañó: marca y modelo por `IN` validado, estado y
 * origen por un OR de LIKEs sobre valores saneados. Con un solo valor elegido la condición que
 * se arma es la misma que había, así que quien llame al endpoint con un valor único obtiene
 * exactamente lo que obtenía.
 *
 * `buildCombustiblesQueryState()`, abajo, traduce lo que entrega el panel al objeto `query`
 * intermedio que la grilla ya tenía: de ahí para adelante no se tocó nada, y las tres consultas
 * que leen esos filtros siguen armando su URL como siempre.
 */

export const COMBUSTIBLES_FILTROS_STORAGE_KEY = 'CombustiblesGrid_filtros';

/** La clave y el shape que usaba la versión con switch "Persistir Filtros". */
export const COMBUSTIBLES_FILTROS_STORAGE_KEY_VIEJA = 'CombustiblesGridFilter';

const ANCHO = { xs: 12, sm: 6, md: 3, lg: 2 };

/**
 * Los campos.
 *
 * Los gates salen de la misma config que ya decidía si el filtro se veía, y se pasan resueltos
 * desde la grilla: un campo con `visible: false` no se renderiza NI aporta su clave al query,
 * así que los flags tienen que estar disponibles en el render 0 (ver la doc del patrón).
 */
export const buildCombustiblesFilterFields = (intl, flags) => {
	const f = flags || {};

	return [
		{
			// El período no viaja como tal: se serializa a periodoInicio / periodoFin, y la
			// grilla los mete en el PATH de la URL (`/combustibles/grid/{inicio}/{fin}`), que es
			// como los esperan estos endpoints.
			name: 'periodo',
			type: 'periodo',
			startName: 'periodoInicio',
			endName: 'periodoFin',
			messageId: 'combustiblesGrid.render.filtros_periodo.label',
			label: 'Período',
			size: ANCHO,
		},
		{
			name: 'base',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_base.label',
			label: 'Base',
			size: ANCHO,
			visible: f.base === true,
		},
		{
			name: 'pais',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_pais.label',
			label: 'Pais',
			size: ANCHO,
			visible: f.pais === true,
		},
		{
			name: 'centroCosto',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_centro_costos.label',
			label: 'Centro de Costos',
			size: ANCHO,
		},
		{
			name: 'region',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_region.label',
			label: 'Región',
			size: ANCHO,
		},
		{
			name: 'subRegion',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_subregion.label',
			label: 'Sub-Región',
			size: ANCHO,
		},
		{
			name: 'dominio',
			type: 'text',
			messageId: 'combustiblesGrid.render.filtros_dominio.label',
			label: 'Dominio',
			size: ANCHO,
		},
		{
			// El panel exige messageId; el control viejo tenía "VIN" escrito a mano, sin i18n.
			// La key es nueva, así que cae en el defaultMessage y se ve exactamente igual.
			name: 'vin',
			type: 'text',
			messageId: 'combustiblesGrid.render.filtros_vin.label',
			label: 'VIN',
			size: ANCHO,
		},
		{
			name: 'marca',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_marca.label',
			label: 'Marca',
			size: ANCHO,
		},
		{
			name: 'modelo',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_modelos.label',
			label: 'Modelos',
			size: ANCHO,
		},
		{
			name: 'estado',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_estado.label',
			label: 'Estado',
			size: ANCHO,
		},
		{
			name: 'lugarCarga',
			type: 'text',
			messageId: 'combustiblesGrid.render.filtros_lugar_de_carga.label',
			label: 'Lugar de Carga',
			size: ANCHO,
		},
		{
			name: 'control',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_controles.label',
			label: 'Controles',
			size: ANCHO,
		},
		{
			name: 'unidad',
			type: 'text',
			messageId: 'combustiblesGrid.render.filtros_unidad.label',
			label: 'Unidad',
			size: ANCHO,
			visible: f.unidad === true,
		},
		{
			name: 'origen',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_origen.label',
			label: 'Origen',
			size: ANCHO,
			visible: f.origen === true,
		},
		{
			name: 'tipoCombustible',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_tipoCombustible.label',
			label: 'Tipo Combustible',
			size: ANCHO,
		},
		{
			name: 'conductorInformadoFilter',
			type: 'text',
			messageId: 'combustiblesGrid.render.filtros_conductor.label',
			label: 'Conductor',
			size: ANCHO,
		},
		{
			name: 'tarjetaFilter',
			type: 'text',
			messageId: 'combustiblesGrid.render.filtros_tarjeta.label',
			label: 'Tarjeta',
			size: ANCHO,
		},
		{
			name: 'proveedorCargaFilter',
			type: 'text',
			messageId: 'combustiblesGrid.render.filtros_proveedor.label',
			label: 'Proveedor',
			size: ANCHO,
		},
		{
			// *** SEMÁNTICA INVERTIDA, COMO EN PERSONAS. *** Prendido quiere decir "incluir los
			// inactivos", y eso se traduce a NO mandar `estadoVehiculo`: el backend sólo filtra
			// por `m.activo` cuando el param viene con valor. Ver la traducción de abajo.
			name: 'verInactivos',
			type: 'check',
			messageId: 'formulariosGridFilters.render.filtros.ver_inactivos',
			label: 'Ver Inactivos',
		},
	];
};

/** El período por defecto que tenía la grilla: del 1 de enero a hoy. */
export const getCombustiblesFilterDefaults = (intl) => {
	const esteAnio = buildPeriodoOptions(intl).filter((o) => o.id === PERIODO_ESTE_ANIO)[0];
	return esteAnio ? { periodo: esteAnio } : {};
};

/**
 * Los flags de config que gatean campos, LEÍDOS DE UNA Y EN EL RENDER 0.
 *
 * *** LOS CUATRO SALEN DE ConfigBusiness, QUE LEE DE MEMORIA. *** `combustibles.unidad.grilla`
 * venía resolviéndose dentro del `Promise.all` que carga las opciones y guardándose en un
 * `useState(false)`: durante el primer render el campo quedaba invisible. Con el panel eso es
 * un bug silencioso —un campo invisible NO aporta su clave al query—, así que un `unidad`
 * persistido no viajaría y la grilla traería de más, sin ningún error. Es la misma trampa que
 * documentó la migración de Móviles.
 */
export const getCombustiblesFilterFlags = () => ({
	base: ConfigBusiness.get('combustibles.base.grilla') === 'true',
	pais: ConfigBusiness.get('combustibles.pais.habilitado') === 'true',
	unidad: ConfigBusiness.get('combustibles.unidad.grilla') === 'true',
	origen: ConfigBusiness.get('combustibles.origen.grilla') === 'true',
});

/**
 * *** LA TRADUCCIÓN AL ESTADO `query` QUE LA GRILLA YA TENÍA. ***
 *
 * La grilla no arma un query string directo desde los controles: pasa por un objeto `query`
 * intermedio, y de ahí salen TRES consultas con formas distintas —`dataTableQuery()` para la
 * grilla, y el builder de `getWidgets()` para el sparkline y las cargas inválidas, que usa
 * `marcas[value]` donde la grilla usa `marcas[0][value]` y manda un subconjunto de los
 * filtros— más el export a Excel.
 *
 * Por eso la migración traduce HASTA ACÁ y no más allá: el panel entrega su query serializado,
 * esto lo convierte en el mismo objeto `query` de siempre, y los tres consumidores quedan
 * intactos. Unificarlos sería otro trabajo, y uno en el que esas diferencias de forma —que son
 * previas— se romperían en silencio.
 *
 * Los valores coinciden exactamente con lo que producía `updateQuery()`:
 * - los múltiples eran `base.map((v) => v.value)`, un ARRAY que al concatenarse da `"1,2"`;
 *   el panel entrega ya ese string, y tanto la concatenación como `searchParams.append()`
 *   producen lo mismo.
 * - los simples eran `marca.value`, que es lo que el panel serializa.
 * - `estadoVehiculo` queda BOOLEANO a propósito: `dataTableQuery()` lo trata como truthy
 *   (false ⇒ manda la clave vacía) y `getWidgets()` lo manda tal cual (false ⇒ "false").
 *   Son dos comportamientos distintos para el mismo check, y los dos son los de hoy.
 */
export const buildCombustiblesQueryState = (q, extra) => {
	const query = q || {};
	const opciones = extra || {};
	const valor = (nombre) => (query[nombre] ? String(query[nombre]) : '');

	return {
		marca: valor('marca'),
		modelo: valor('modelo'),
		base: valor('base'),
		pais: valor('pais'),
		centroCosto: valor('centroCosto'),
		region: valor('region'),
		subRegion: valor('subRegion'),
		dominio: valor('dominio'),
		vin: valor('vin'),
		unidad: valor('unidad'),
		estado: valor('estado'),
		control: valor('control'),
		origen: valor('origen'),
		tipoCombustible: valor('tipoCombustible'),
		lugarCarga: valor('lugarCarga'),
		conductorInformadoFilter: valor('conductorInformadoFilter'),
		tarjetaFilter: valor('tarjetaFilter'),
		proveedorCargaFilter: valor('proveedorCargaFilter'),

		// El check dice "ver inactivos": prendido, el filtro por `m.activo` deja de viajar y
		// entran todos. El panel serializa '1' cuando está prendido, así que acá se invierte.
		estadoVehiculo: query.verInactivos !== '1',

		// El período no es un filtro más: estos endpoints lo reciben en el PATH.
		//
		// *** VACÍO VA COMO `null`, NO COMO ''. *** Con "Todo" el panel devuelve el rango
		// vacío, pero la URL se arma concatenando: `'/' + inicio` con '' produce `//`, que
		// no matchea la ruta `{fechaInicio}/{fechaFin}`. La versión anterior guardaba `null`
		// y mandaba el literal "null" en el path; se conserva tal cual, porque cambiarlo es
		// cambiarle el contrato a tres endpoints.
		filtroPeriodoInicio: valor('periodoInicio') || null,
		filtroPeriodoFin: valor('periodoFin') || null,

		// No sale del panel: viene del "ver cargas" de un móvil.
		movilDetail: opciones.movilDetail !== undefined ? opciones.movilDetail : '',
	};
};

/**
 * El query que produce el panel con la vista por defecto, armado con SUS reglas.
 *
 * Lo necesitan dos cosas: decidir si hay que mostrar la franja de resultados, y el reset de
 * la ✕ —que tiene que devolver la CONSULTA a su estado inicial, no sólo los controles—.
 */
export const buildCombustiblesQueryPorDefecto = (intl, flags) => {
	const campos = buildCombustiblesFilterFields(intl, flags).filter((c) => c.visible !== false);
	return serializeFilterState(
		campos,
		Object.assign({}, buildEmptyFilterState(campos), getCombustiblesFilterDefaults(intl))
	);
};

/**
 * ¿Lo que se está consultando es una BÚSQUEDA, o es la vista por defecto?
 *
 * Es lo que decide si se ve la franja verde de resultados. No alcanza con "hay alguna clave
 * con valor", que es como lo resuelven otras grillas del patrón: acá el default NO es vacío
 * —el período arranca en "Este año"— así que esa regla daría siempre verdadero.
 *
 * Se recalcula en cada aplicación, incluida la del montaje: por eso al reingresar con filtros
 * guardados la franja aparece sola, sin esperar a que el usuario apriete "Buscar".
 */
export const esBusqueda = (query, intl, flags) => {
	const porDefecto = buildCombustiblesQueryPorDefecto(intl, flags);
	const actual = query || {};
	const claves = Object.keys(porDefecto).concat(Object.keys(actual));

	return claves.some((k) => String(actual[k] || '') !== String(porDefecto[k] || ''));
};
